import React, {useState, useEffect, useRef} from 'react'
import PropTypes from 'prop-types'
import {Text} from '@salesforce/retail-react-app/app/components/shared/ui'
import {DateTime} from 'luxon'

const CountDownTimer = ({
    targetDate,
    variantStyles,
    type = 'default',
    tz = 'America/Chicago', // default to CT
    tag = 'h2'
}) => {
    const formatNumber = (num) => String(num).padStart(2, '0')
    const calculateTimeLeft = () => {
        if (!targetDate) return new Date()
        const {dateVal, hour, minute, second} = targetDate

        // parse date and time with timezone using luxon
        const countdownDateTime = DateTime.fromFormat(
            `${dateVal} ${formatNumber(hour)}:${formatNumber(minute)}:${formatNumber(second)}`,
            'MM/dd/yyyy HH:mm:ss',
            {zone: tz}
        )

        const now = DateTime.now().setZone(tz)
        const diff = countdownDateTime.diff(now, ['days', 'hours', 'minutes', 'seconds'])

        if (diff <= 0) return null

        const days = Math.floor(diff / (1000 * 60 * 60 * 24))
        const hours = Math.floor((diff / (1000 * 60 * 60)) % 24)
        const minutes = Math.floor((diff / (1000 * 60)) % 60)
        const seconds = Math.floor((diff / 1000) % 60)

        return {days, hours, minutes, seconds}
    }

    const [timeLeft, setTimeLeft] = useState(calculateTimeLeft())
    const timerRef = useRef(null)

    useEffect(() => {
        if (!timeLeft) return

        timerRef.current = setInterval(() => {
            const newTimeLeft = calculateTimeLeft()
            setTimeLeft(newTimeLeft)
        }, 1000)

        return () => clearInterval(timerRef.current)
    }, [timeLeft])

    const daySuffix = timeLeft?.days > 1 ? 'days' : 'day'
    const hrSuffx = timeLeft?.hours > 1 ? 'hrs' : 'hr'

    let numStyles = type === 'default' ? variantStyles.headline : variantStyles.eyeBrow
    let labelStyles = type === 'default' ? variantStyles.label : variantStyles.eyeBrowLabel

    if (type === 'default') {
        numStyles['fontFamily'] = 'caslon' // ensure fontFamily for numStyles
    }

    // if numStyles are provided in variantStyles, use them
    if (variantStyles.numStyles) {
        numStyles = variantStyles.numStyles
    }

    // if timer font are provided in variantStyles, use them
    if (variantStyles.timer) {
        numStyles.fontFamily = variantStyles.timer.fontFamily
        labelStyles.fontFamily = variantStyles.timer.fontFamily
    }

    // fallback to avoid rendering issues
    const countDownTimeLeft = timeLeft || {days: 0, hours: 0, minutes: 0, seconds: 0}

    return (
        <Text className="countdown-timer" as={tag} {...variantStyles.box}>
            <Text className="countdown-timer-days" as="span" display="inline-block" {...numStyles}>
                {countDownTimeLeft.days}
            </Text>
            <Text
                className="countdown-timer-days-label"
                as="span"
                display="inline-block"
                {...labelStyles}
            >
                {daySuffix}
            </Text>
            <Text className="countdown-timer-hours" as="span" display="inline-block" {...numStyles}>
                {countDownTimeLeft.hours}
            </Text>
            <Text
                className="countdown-timer-hours-label"
                as="span"
                display="inline-block"
                {...labelStyles}
            >
                {hrSuffx}
            </Text>
            <Text
                className="countdown-timer-minutes"
                as="span"
                display="inline-block"
                {...numStyles}
            >
                {countDownTimeLeft.minutes}
            </Text>
            <Text
                className="countdown-timer-minutes-label"
                as="span"
                display="inline-block"
                {...labelStyles}
            >
                min
            </Text>
            <Text
                className="countdown-timer-seconds"
                as="span"
                minWidth={numStyles.fontSize}
                display="inline-block"
                {...numStyles}
            >
                {countDownTimeLeft.seconds}
            </Text>
            <Text
                className="countdown-timer-seconds-label"
                as="span"
                display="inline-block"
                {...labelStyles}
            >
                sec
            </Text>
        </Text>
    )
}

export default CountDownTimer

CountDownTimer.propTypes = {
    targetDate: PropTypes.object.isRequired,
    variantStyles: PropTypes.object,
    type: PropTypes.string,
    tz: PropTypes.string,
    tag: PropTypes.string
}
